import { CurrentUserContext } from "@/app/app.utils";
import { Flex, styledStitches } from "@/app/design-system";
import { SolutionOutlined } from "@ant-design/icons";
import { useContext } from "react";

import { NavigationMenuItem } from "../../components.utility/navigation-menu-item";
import { useQueryClient } from "react-query";
import { usePatchClinician } from "@/app/hooks/use-patch-clinician";
import { useIsPathwaysMilestone3 } from "@/app/hooks/use-is-pathways-milestone3";
import { getMyConsultsPageTitle } from "@/app/consults-and-matching/MatchingV2/_utils";

interface MyConsultsMenuItemProps {
  isCollapsed: boolean;
}

// NOTE: this is translated from the tailwind css in Roofdog
const NotificationDotParent = styledStitches("div", {
  position: "absolute",
  top: "1px",
  right: "-2px",
  "& span.ping": {
    backgroundColor: "#639568",
    borderRadius: "1.625rem",
    width: "0.5rem",
    height: "0.5rem",
    position: "absolute",
    display: "block",
  },
  "& span.dot": {
    display: "block",
    width: "0.5rem",
    height: "0.5rem",
    backgroundColor: "#639568",
    borderRadius: "1.625rem",
  },
});

const NotificationDot = () => (
  <NotificationDotParent>
    <span className="ping" />
    <span className="dot" />
  </NotificationDotParent>
);

export function MyConsultsMenuItem({ isCollapsed }: MyConsultsMenuItemProps) {
  const currentUser = useContext(CurrentUserContext);
  const queryClient = useQueryClient();
  const patchClinicianAPI = usePatchClinician();
  const isPathwaysMilestone3 = useIsPathwaysMilestone3();
  const myConsultsLabel = getMyConsultsPageTitle(isPathwaysMilestone3);

  const hasNewDynamicallyScheduledConsult =
    currentUser?.clinician?.ui_flags?.new_dynamically_booked_consult ?? false;

  const onMyConsultsClick = async () => {
    if (currentUser?.clinician?.ui_flags?.new_dynamically_booked_consult) {
      await patchClinicianAPI.mutateAsync(
        {
          clinicianId: currentUser.clinician.id,
          body: {
            ui_flags: {
              ...currentUser.clinician.ui_flags,
              new_dynamically_booked_consult: false,
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["currentUser"] });
          },
        },
      );
    }
  };

  return (
    <NavigationMenuItem
      to="/matching"
      menuItemTooltipContent={myConsultsLabel}
      isCollapsed={isCollapsed}
      onClickFunction={onMyConsultsClick}
    >
      <Flex
        justifyContent={"space-between"}
        css={{ flexGrow: isCollapsed ? 0 : 1, position: "relative" }}
      >
        <Flex gap={12}>
          <div
            style={{
              position: hasNewDynamicallyScheduledConsult
                ? "relative"
                : undefined,
            }}
          >
            <SolutionOutlined
              height={20}
              width={20}
              aria-hidden={!isCollapsed}
              title={!isCollapsed ? "" : myConsultsLabel}
            />
            {hasNewDynamicallyScheduledConsult && <NotificationDot />}
          </div>
          {!isCollapsed ? myConsultsLabel : null}
        </Flex>
      </Flex>
    </NavigationMenuItem>
  );
}
